/**
 * Section F Page - F.重複或再次登記確認說明
 * Confirmation of avoidance of double or revived registration
 * UI 模式：2 頁籤（F.1 勾選框 / F.2 Radio + 條件富文本）
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import sectionFService, { SectionFDetail } from '../services/sectionFService';
import FunctionPageHeader from '../components/FunctionPageHeader';

// ── SaveFeedback ──────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ── CheckItem ─────────────────────────────────────────────────────
interface CheckItemProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
  subLabel?: string;
}
const CheckItem: React.FC<CheckItemProps> = ({ checked, onChange, disabled, label, subLabel }) => (
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: disabled ? 'default' : 'pointer', marginBottom: 12 }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      disabled={disabled}
      style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0, cursor: disabled ? 'default' : 'pointer', accentColor: '#16a085' }}
    />
    <span style={{ fontSize: 14, lineHeight: 1.6, color: '#333' }}>
      <strong>{label}</strong>
      {subLabel && <span style={{ display: 'block', fontSize: 12, color: '#888', marginTop: 2 }}>{subLabel}</span>}
    </span>
  </label>
);

// ── RadioItem ─────────────────────────────────────────────────────
interface RadioItemProps {
  value: number;
  current: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
  label: string;
  subLabel?: string;
}
const RadioItem: React.FC<RadioItemProps> = ({ value, current, onChange, disabled, label, subLabel }) => (
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: disabled ? 'default' : 'pointer', marginBottom: 14 }}>
    <input
      type="radio"
      checked={current === value}
      onChange={() => onChange(value)}
      disabled={disabled}
      style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0, cursor: disabled ? 'default' : 'pointer', accentColor: '#16a085' }}
    />
    <span style={{ fontSize: 14, lineHeight: 1.6, color: '#333' }}>
      <strong>{label}</strong>
      {subLabel && <span style={{ display: 'block', fontSize: 12, color: '#888', marginTop: 2 }}>{subLabel}</span>}
    </span>
  </label>
);

// ── Hint Box ──────────────────────────────────────────────────────
const HintBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ marginBottom: 16, padding: '10px 14px', background: '#e6f7ff', border: '1px solid #91caff', borderRadius: 6, fontSize: 13, color: '#0958d9' }}>
    ℹ {children}
  </div>
);

// ── InfoBox ───────────────────────────────────────────────────────
const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 13, color: '#874d00' }}>
    {children}
  </div>
);

// ════════════════════════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════════════════════════
type FTab = 'f1' | 'f2';

const SectionFPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission('section_f', 'update');

  const [detail, setDetail] = useState<SectionFDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<FTab>('f1');
  const [resetConfirm, setResetConfirm] = useState(false);

  // F.1 local state
  const [f1NotRegistered, setF1NotRegistered]       = useState(false);
  const [f1NotIncludedAsCp, setF1NotIncludedAsCp]   = useState(false);
  const [f1NotDeregistered, setF1NotDeregistered]   = useState(false);
  const [f1WasExcludedCp, setF1WasExcludedCp]       = useState(false);
  const [f1ExclusionDetails, setF1ExclusionDetails] = useState('');

  // F.2 local state
  const [f2Status, setF2Status]               = useState<number | null>(null);
  const [f2Confirmation, setF2Confirmation]   = useState('');

  const { msg: msgF1, show: showF1 } = useSaveFeedback();
  const { msg: msgF2, show: showF2 } = useSaveFeedback();
  const [savingF1, setSavingF1] = useState(false);
  const [savingF2, setSavingF2] = useState(false);

  // ── Sync detail → local state ─────────────────────────────────
  const syncDetail = (d: SectionFDetail | null) => {
    setF1NotRegistered(d?.a64_not_registered ?? false);
    setF1NotIncludedAsCp(d?.a64_not_included_as_cp ?? false);
    setF1NotDeregistered(d?.a64_not_deregistered ?? false);
    setF1WasExcludedCp(d?.a64_was_excluded_cp ?? false);
    setF1ExclusionDetails(d?.a64_exclusion_details ?? '');
    setF2Status(d?.other_crediting_status ?? null);
    setF2Confirmation(d?.other_confirmation_details ?? '');
  };

  // ── Load ──────────────────────────────────────────────────────
  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionFPage');
      const res = await sectionFService.getSectionFByProject(projectId);
      setDetail(res);
      syncDetail(res);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setDetail(null); syncDetail(null); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  // ── Save F.1 ──────────────────────────────────────────────────
  const handleSaveF1 = async () => {
    if (!selectedProjectId) return;
    setSavingF1(true);
    try {
      const res = await sectionFService.saveSectionF(selectedProjectId, {
        a64_not_registered:     f1NotRegistered,
        a64_not_included_as_cp: f1NotIncludedAsCp,
        a64_not_deregistered:   f1NotDeregistered,
        a64_was_excluded_cp:    f1WasExcludedCp,
        a64_exclusion_details:  f1WasExcludedCp ? f1ExclusionDetails : null,
      });
      setDetail(res);
      showF1(true, t('sectionF.saveSuccess'));
    } catch {
      showF1(false, t('sectionF.saveFailed'));
    } finally { setSavingF1(false); }
  };

  // ── Save F.2 ──────────────────────────────────────────────────
  const handleSaveF2 = async () => {
    if (!selectedProjectId) return;
    setSavingF2(true);
    try {
      const res = await sectionFService.saveSectionF(selectedProjectId, {
        other_crediting_status:     f2Status,
        other_confirmation_details: (f2Status === 2 || f2Status === 3) ? f2Confirmation : null,
      });
      setDetail(res);
      showF2(true, t('sectionF.saveSuccess'));
    } catch {
      showF2(false, t('sectionF.saveFailed'));
    } finally { setSavingF2(false); }
  };

  // ── Reset ─────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!selectedProjectId) return;
    try {
      await sectionFService.resetSectionF(selectedProjectId);
      setDetail(null);
      syncDetail(null);
    } catch { /* ignore */ }
    setResetConfirm(false);
  };

  // ── Tab style ─────────────────────────────────────────────────
  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 22px', fontSize: 14, fontWeight: active ? 700 : 500,
    background: active ? '#16a085' : '#f5f5f5',
    color: active ? '#fff' : '#555',
    cursor: 'pointer',
    borderTop: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    borderBottom: active ? '2px solid #fff' : '1px solid #e0e0e0',
    borderRadius: '6px 6px 0 0',
    marginRight: 4, transition: 'all 0.15s',
  });

  const saveBtn = (saving: boolean, onClick: () => void): React.CSSProperties => ({
    padding: '8px 24px', fontSize: 14, borderRadius: 6, border: 'none',
    background: saving ? '#ccc' : '#16a085', color: '#fff',
    cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600,
  });

  // ── F.2 conditional hint ──────────────────────────────────────
  const f2HintText = () => {
    if (f2Status === 2) return isZh
      ? '請向其他計入機制取得確認文件，包含從該機制撤銷登記或排除的生效日期，以及撤銷/排除時剩餘的計入期。[Obtain a confirmation of the other crediting scheme of the effective date of deregistration or exclusion from the other crediting scheme and remaining crediting period under the other crediting scheme at the time of deregistration or exclusion.]'
      : 'Obtain a confirmation of the other crediting scheme of the effective date of deregistration or exclusion from the other crediting scheme and remaining crediting period under the other crediting scheme at the time of deregistration or exclusion.';
    if (f2Status === 3) return isZh
      ? '請向其他計入機制取得確認文件，包含登記或涵蓋生效日期、計入期起迄日期，以及已在該機制下核發額度的監測期間。[Obtain a confirmation of the other crediting scheme of the effective date of the registration or coverage, the start and end dates of the crediting period, and the monitoring periods for which credits have been issued under the other crediting scheme.]'
      : 'Obtain a confirmation of the other crediting scheme of the effective date of the registration or coverage, the start and end dates of the crediting period, and the monitoring periods for which credits have been issued under the other crediting scheme.';
    return null;
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_f" />
        {canUpdate && detail && (
          <button onClick={() => setResetConfirm(true)}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>
            🗑 {t('sectionF.resetData')}
          </button>
        )}
      </div>

      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>{t('sectionF.selectProject')}</div>
      ) : loading ? (
        <div className="loading">{t('sectionF.loading')}</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>

          {/* ── Tabs ── */}
          <div style={{ display: 'flex', padding: '16px 20px 0', borderBottom: '1px solid #e0e0e0', background: '#fafafa', borderRadius: '8px 8px 0 0' }}>
            <button style={tabStyle(activeTab === 'f1')} onClick={() => setActiveTab('f1')}>
              {t('sectionF.tabF1')}
            </button>
            <button style={tabStyle(activeTab === 'f2')} onClick={() => setActiveTab('f2')}>
              {t('sectionF.tabF2')}
            </button>
          </div>

          {/* ── F.1 Content ── */}
          {activeTab === 'f1' && (
            <div style={{ padding: '24px 28px' }}>

              {/* Mandatory checkboxes */}
              <div style={{ marginBottom: 6, fontWeight: 700, fontSize: 14, color: '#16a085' }}>
                {t('sectionF.f1MandatoryTitle')}
              </div>
              <div style={{ marginBottom: 4 }}>
                <CheckItem
                  checked={f1NotRegistered}
                  onChange={setF1NotRegistered}
                  disabled={!canUpdate}
                  label={isZh
                    ? '提議的 A6.4 專案尚未登記為 A6.4 專案'
                    : 'The proposed A6.4 project has not been already registered as an A6.4 project.'}
                />
                <CheckItem
                  checked={f1NotIncludedAsCp}
                  onChange={setF1NotIncludedAsCp}
                  disabled={!canUpdate}
                  label={isZh
                    ? '提議的 A6.4 專案尚未作為 CP 納入已登記的 Article 6.4 機制 PoA'
                    : 'The proposed A6.4 project has not been already included as a component project (CP) in a registered Article 6.4 mechanism programme of activities (A6.4 PoA).'}
                />
                <CheckItem
                  checked={f1NotDeregistered}
                  onChange={setF1NotDeregistered}
                  disabled={!canUpdate}
                  label={isZh
                    ? '提議的 A6.4 專案未曾從 Article 6.4 機制撤銷登記'
                    : 'The proposed A6.4 project has not been previously deregistered from the Article 6.4 mechanism.'}
                />
              </div>
              <HintBox>
                {isZh
                  ? '請勾選以上三個勾選框，以確認符合強制要求。[Tick all the three boxes above as a confirmation of compliance with mandatory requirements.]'
                  : 'Tick all the three boxes above as a confirmation of compliance with mandatory requirements.'}
              </HintBox>

              {/* Divider */}
              <div style={{ borderTop: '1px dashed #e0e0e0', margin: '20px 0' }} />

              {/* Optional checkbox */}
              <div style={{ marginBottom: 6, fontWeight: 700, fontSize: 14, color: '#555' }}>
                {t('sectionF.f1OptionalTitle')}
              </div>
              <CheckItem
                checked={f1WasExcludedCp}
                onChange={v => { setF1WasExcludedCp(v); if (!v) setF1ExclusionDetails(''); }}
                disabled={!canUpdate}
                label={isZh
                  ? '提議的 A6.4 專案曾為已登記 A6.4 PoA 下的 CP，但已被排除'
                  : 'The proposed A6.4 project was a CP in a registered A6.4 PoA but has been previously excluded.'}
              />
              <HintBox>
                {isZh ? '如適用請勾選。[Tick the box if applicable.]' : 'Tick the box if applicable.'}
              </HintBox>

              {/* Conditional rich text */}
              {f1WasExcludedCp && (
                <div style={{ marginTop: 4 }}>
                  <InfoBox>
                    {isZh
                      ? '請描述以下內容：① 是否係自願排除或因錯誤納入而排除；② A6.4 PoA 的 UNFCCC 登記號碼；③ CP 編號及納入日期；④ 計入期類型（可再生或固定）及排除時的計入期截止日期。'
                      : 'If this box is ticked, describe: (i) Whether the project was excluded from the A6.4 PoA voluntarily or due to erroneous inclusion; (ii) The UNFCCC registration number of the A6.4 PoA; (iii) The CP number and the inclusion date; and (iv) The crediting period type (i.e. renewable or fixed) and the end date of the crediting period valid for the CP at the time of the exclusion.'}
                  </InfoBox>
                  <RichTextEditor value={f1ExclusionDetails} onChange={setF1ExclusionDetails} disabled={!canUpdate} minHeight="180px" />
                </div>
              )}

              {/* Save */}
              {canUpdate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
                  <button onClick={handleSaveF1} disabled={savingF1} style={saveBtn(savingF1, handleSaveF1)}>
                    {savingF1 ? t('sectionF.saving') : t('sectionF.save')}
                  </button>
                  {msgF1 && <span style={{ fontSize: 12, color: msgF1.ok ? '#52c41a' : '#e53935' }}>{msgF1.text}</span>}
                </div>
              )}
            </div>
          )}

          {/* ── F.2 Content ── */}
          {activeTab === 'f2' && (
            <div style={{ padding: '24px 28px' }}>

              <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 14, color: '#16a085' }}>
                {t('sectionF.f2SelectTitle')}
              </div>

              <RadioItem
                value={1}
                current={f2Status}
                onChange={v => { setF2Status(v); setF2Confirmation(''); }}
                disabled={!canUpdate}
                label={isZh
                  ? '提議的 A6.4 專案目前未在任何其他 GHG 減量計入機制下登記、申請登記或受計畫涵蓋'
                  : 'The proposed A6.4 project is not currently registered or being pursued for registration, or covered by a programme, under any other international, regional, national, subnational or sector-wide GHG mitigation crediting scheme.'}
              />
              <RadioItem
                value={2}
                current={f2Status}
                onChange={setF2Status}
                disabled={!canUpdate}
                label={isZh
                  ? '提議的 A6.4 專案曾在其他 GHG 減量計入機制下登記或受計畫涵蓋，但在充分使用計入期之前已撤銷登記或排除'
                  : 'The proposed A6.4 project was previously registered under or covered by a programme under any other international, regional, national, or subnational or sector-wide GHG mitigation crediting scheme but deregistered or excluded from the other crediting scheme before fully consuming the crediting period under the other crediting scheme.'}
              />
              <RadioItem
                value={3}
                current={f2Status}
                onChange={setF2Status}
                disabled={!canUpdate}
                label={isZh
                  ? '提議的 A6.4 專案目前已在其他 GHG 減量計入機制下登記或受涵蓋'
                  : 'The proposed A6.4 project is currently registered or covered by other international, regional, national, subnational or sector-wide GHG mitigation crediting scheme.'}
              />

              <HintBox>
                {isZh ? '請僅勾選一個適用選項。[Tick only one applicable box.]' : 'Tick only one applicable box.'}
              </HintBox>

              {/* Conditional rich text for option 2 or 3 */}
              {(f2Status === 2 || f2Status === 3) && (
                <div style={{ marginTop: 4 }}>
                  <InfoBox>{f2HintText()}</InfoBox>
                  <RichTextEditor value={f2Confirmation} onChange={setF2Confirmation} disabled={!canUpdate} minHeight="180px" />
                </div>
              )}

              {/* Save */}
              {canUpdate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
                  <button onClick={handleSaveF2} disabled={savingF2} style={saveBtn(savingF2, handleSaveF2)}>
                    {savingF2 ? t('sectionF.saving') : t('sectionF.save')}
                  </button>
                  {msgF2 && <span style={{ fontSize: 12, color: msgF2.ok ? '#52c41a' : '#e53935' }}>{msgF2.text}</span>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reset confirm */}
      {resetConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('sectionF.resetTitle')}</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20, whiteSpace: 'pre-line' }}>{t('sectionF.resetConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setResetConfirm(false)} style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>{t('sectionF.cancel')}</button>
              <button onClick={handleReset} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>{t('sectionF.reset')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionFPage;
